/* eslint semi: 1 */

import _ from 'lodash';
import moment from 'moment-timezone';

import { proxyException, EitherResponse } from './player-setup-communication';

import DialogPopup from '@/components/common/dialog-popup/dialog-popup-tmpl';
import { EventTypes, CLOSE_DIALOG } from '@/components/common/dialog-popup/dialog-popup';
import DialogPopupPage from '@/components/common/dialog-popup/dialog-popup-page/dialog-popup-page-tmpl';
import PlayerSetupPlanSummary from './player-setup-plan-summary/player-setup-plan-summary-tmpl';
import PlayerSetupSerial from './player-setup-serial/player-setup-serial-tmpl';
import PlayerSetupPin from './player-setup-pin/player-setup-pin-tmpl';
import PlayerSetupName from './player-setup-name/player-setup-name-tmpl';
import PlayerSetupDisplay from './player-setup-display/player-setup-display-tmpl';
import PlayerSetupApplication from './player-setup-application/player-setup-application-tmpl';
import { PLAYER_APPLICATIONS } from './player-setup-application/player-setup-application';
import PlayerSetupLed from './player-setup-adjustments/player-setup-led/player-setup-led-tmpl';
import PlayerSetupVideowall from './player-setup-adjustments/player-setup-videowall/player-setup-videowall-tmpl';
import PlayerSetupIcon from './player-setup-icon/player-setup-icon-tmpl';
import PlayerSetupArrangement from './player-setup-arrangement/player-setup-arrangement-tmpl';

import { PlayerBuilder } from './player-data-builder';

import {
  PLAYER_SETUP_STEPS,
  PROGRESS_SCENARIOS,
  PROGRESS_SCENARIO_OPTIONS,
  CLOSE_SETUP_EVENT
} from './player-setup-common';

import {
  submitSerialForm,
  submitPinForm,
  submitNameForm,
  submitDisplayForm,
  submitApplicationForm,
  submitArrangementForm,
  submitAdjustmentsForm
} from './player-setup-form-handlers';

export const FORM_HANDLERS = {
  submitSerialForm,
  submitPinForm,
  submitNameForm,
  submitDisplayForm,
  submitApplicationForm,
  submitArrangementForm,
  submitAdjustmentsForm
};

export default {
  name: 'player-setup',
  inject: [
    '$utils',
    '$api'
  ],
  components: {
    DialogPopup,
    DialogPopupPage,
    PlayerSetupIcon,

    // pages
    PlayerSetupPlanSummary,
    PlayerSetupSerial,
    PlayerSetupPin,
    PlayerSetupName,
    PlayerSetupApplication,
    PlayerSetupDisplay,
    PlayerSetupArrangement,
    PlayerSetupLed,
    PlayerSetupVideowall
  },
  props: {
    isOpened: {
      type: Boolean,
      default: false
    },
    serialNumber: {
      type: String,
      default: () => ''
    },
    userPlan: {
      type: Object,
      default: () => {
        return {
          premium: false,
          enterprise: false
        };
      }
    },
    isRestart: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    // bind form handlers to `this`, so it is available within boundaries of a handler
    const handlers = _.fromPairs(_.map(FORM_HANDLERS, (fn, key) => [key, fn.bind(this)]));

    return {
      handlers,
      isNewPlayer: false,
      isAryaPlayer: false,
      form: {
        serial: ''
      },
      validationErrors: {
        serialForm: '',
        nameForm: '',
        displayForm: '',
        adjustmentsForm: ''
      },
      // NOTE: `player` property is a copy of playerBuilder.getPlayer().
      // The reason is that Vue should propogate changes in playerBuilder
      // somehow to downstream components and it is not possible to on non-raw objects.
      player: null,
      playerBuilder: new PlayerBuilder(),
      // TODO: describe player shape

      // related to progress scenarios
      progressScenarioId: null
    };
  },
  mounted () {
    this.playerBuilder.reset(null);
  },
  watch: {
    serialNumber: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.form.serial = newVal;
        }
      }
    },
    isOpened: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal && oldVal !== newVal) {
          this.player = null;
          this.playerBuilder.reset(null);
        }
      }
    },
    isRestart: {
      immediate: true,
      async handler (newVal) {
        if (newVal) {
          const { error } = await this.getPlayerData();
          if (!error) this.switchPageAction(this.steps.STEP_NAME);
        }
      }
    }
  },
  computed: {
    steps () {
      return PLAYER_SETUP_STEPS;
    },
    progress () {
      return PROGRESS_SCENARIOS;
    },
    // player data.serialNumber OR form.serial OR EMPTY STRING
    serial () {
      return this.player?.getSerialNumber() ?? this.form?.serial;
    },
    name () {
      return this.player?.getName() ?? '';
    },
    timezone () {
      // guess timezone or use Europe/Zurich as a default one
      const defaultTimezone = moment.tz.guess(true) ?? 'Europe/Zurich';
      return this.player?.getTimezone() ?? defaultTimezone;
    },
    applicationType () {
      return this.player?.getApplicationType() ?? null;
    },
    resolution () {
      return this.player?.getResolution() ?? '';
    },
    orientation () {
      return this.player?.getOrientation() ?? 'horizontal';
    },
    outputs () {
      return this.player?.getOutputCount();
    },
    deviceType () {
      return this.player?.getDeviceType() ?? '';
    },
    licenses () {
      return this.player?.getLicenses() ?? [];
    },
    isIconVisible () {
      return this.hasPlayerConfiguration();
    },
    resolutions () {
      const resolutions = this.player?.getResolutions();
      if (!resolutions || resolutions.length === 0) {
        return this.playerBuilder.getDefaultResolutions();
      }

      return resolutions;
    },
    selectedOutputCount () {
      return this.player?.getOutput();
    },
    arrangement () {
      return this.player?.getArrangement();
    },
    canvasSize () {
      if (!this.player) {
        return null;
      }

      const canvas = this.player?.getCanvasAsObject();
      if (!canvas.width && !canvas.height) {
        return null;
      }
      return `${canvas.width} ${canvas.height}`;
    },
    isLEDAvailable () {
      return this.userPlan.enterprise && this.hasMultiOutput();
    },
    isPremiumPlan () {
      return this.userPlan.premium;
    },
    isDiscovery () {
      // NOT premium and NOT enterprise
      return !this.userPlan.premium && !this.userPlan.enterprise;
    },
    isPlanSummaryVisible () {
      return this.isPremiumPlan && !this.isRestart;
    },
    PLAYER_APPLICATIONS () {
      return PLAYER_APPLICATIONS;
    }
  },
  methods: {
    hasMultiOutput () {
      return this.outputs > 1;
    },
    hasPlayerConfiguration () {
      return this.player?.hasData() ?? false;
    },
    updatePlayer () {
      this.player = null;
      this.player = this.playerBuilder.getPlayer();
    },
    async switchPageAction (page) {
      // propogate all properties to downstream components before switch
      this.updatePlayer();

      // reset form validation errors
      Object.keys(this.validationErrors).forEach(key => {
        this.validationErrors[key] = '';
      });

      // I want to make sure that properties are populated BEFORE page switch
      await this.$nextTick();
      await this.$refs.dialog.switchPageAction(page);
    },
    resetPlayerBuilder (data, callback) {
      // NOTE: explicitly reset player data, this is required to trigger props propagation
      this.playerBuilder.reset(data);
      if (callback) {
        callback(this.playerBuilder);
      }
      this.player = this.playerBuilder.getPlayer();
    },
    async getPlayerData () {
      const { ok: response, error } = await proxyException(() => this.$api.get(`${CONFIG.API_BASE_URL}/player/${this.serial}`));

      if (error) {
        return EitherResponse(null, error);
      }

      this.resetPlayerBuilder(response.data);
      return EitherResponse(response);
    },
    /**
     * Progress Scenario flow control functionality.
     * When there's specific option is selected, then provide
     * respective `progressScenarioId` on page change.
     */
    handlePageChange () {
      if (!this.hasPlayerConfiguration()) {
        return;
      }

      if (this.hasMultiOutput()) {
        this.progressScenarioId = PROGRESS_SCENARIO_OPTIONS.MULTI_OUTPUTS;

        if (this.applicationType === PLAYER_APPLICATIONS.APP_LED) {
          this.progressScenarioId = PROGRESS_SCENARIO_OPTIONS.LED;
        } else if (this.applicationType === PLAYER_APPLICATIONS.APP_MIRRORING) {
          this.progressScenarioId = PROGRESS_SCENARIO_OPTIONS.MIRRORING;
        } else if (this.applicationType === PLAYER_APPLICATIONS.APP_VIDEOWALL) {
          this.progressScenarioId = PROGRESS_SCENARIO_OPTIONS.VIDEO_WALL;
        }
      } else {
        this.progressScenarioId = PROGRESS_SCENARIO_OPTIONS.SINGLE_DISPLAY;
      }
    },
    getCurrentScenarioIndex (currentPageId) {
      if (!this.progressScenarioId) {
        return [null, null];
      }
      const scenario = PROGRESS_SCENARIOS[this.progressScenarioId];
      return [scenario, scenario.findIndex(s => s.id === currentPageId)];
    },
    /**
     * Function to determine what page should go next based on two inputs:
     *   1. $data.progressScenarioId
     *   2. pageId
     * @param {string} currentPageId - page to calculate next scenario ID
     */
    nextPage (currentPageId) {
      const [scenario, index] = this.getCurrentScenarioIndex(currentPageId);
      if (!index || scenario.length <= index + 1) {
        return CLOSE_DIALOG;
      }
      // return next page ID
      return scenario[index + 1].id;
    },
    prevPage (currentPageId) {
      const [scenario, index] = this.getCurrentScenarioIndex(currentPageId);
      if (!index) {
        return CLOSE_DIALOG;
      } else if (index === 0) {
        return scenario[0].id;
      }
      return scenario[index - 1].id;
    },
    async applyAdjustments () {
      const { data: applicationData } = await this.$refs.pageAdjustmentsVideowall.submitForm();

      if (!applicationData) {
        return;
      }

      if (applicationData.data.bezels) {
        this.playerBuilder.setBezels(
          Number(applicationData.data.bezels.screenDiagonal),
          Number(applicationData.data.bezels.verticalBezel),
          Number(applicationData.data.bezels.horizontalBezel)
        );

        // call updatePlayer to trigger updates for computed properties
        this.updatePlayer();
      }
    },
    async commitPlayerConfiguration (tag) {
      const assignPlayerUrl = `${CONFIG.API_BASE_URL}/player`;
      const url = `${CONFIG.API_BASE_URL}/player/${this.serial}`;

      const data = this.playerBuilder.getPlayer().getConfigRequestData();
      const tags = this.playerBuilder.getPlayer().getTags().filter((tag) => !tag.startsWith('LED:'));

      // preserve previous tags
      data.tags = tags;

      // associate with proper new LED tags
      if (_.isString(tag) && tag !== '') {
        data.tags.push(tag);
      }

      // If player is not new for the account and the player is
      // not in ARYA, perform a POST request to `/player` that
      // assigns the player with `addToArya: true` flag.
      if (!this.isNewPlayer && !this.isAryaPlayer) {
        const result = await proxyException(() => this.$api.post(assignPlayerUrl, {
          serialNumber: this.serial,
          addToArya: true
        }));

        if (result.error) {
          return EitherResponse(null, result.error.response?.data?.message ?? result.error.message);
        }

        this.isAryaPlayer = true;
      }

      // Perform PUT request to update player configuration
      const { ok: response, error } = await proxyException(() => this.$api.put(url, data));

      if (error) {
        return EitherResponse(null, error.response?.data?.message ?? error.message);
      }

      // NOTE: we need to keep player and playerBuilder in sync
      // WARN: if commitPlayerConfiguration is NOT a last call before the Wizard close
      // then you would want to reset player data with results from response.data
      return EitherResponse(response.data);
    },
    async handlePlanConfirmation () {
      const { nextPage } = this.$refs.pagePlanSummary.confirmPlanChange();
      await this.switchPageAction(nextPage);
    },
    handleSetApplication (application) {
      this.playerBuilder.setApplicationType(application);
      this.updatePlayer();
      this.handlePageChange();
    },
    handleBackButton (currentPage) {
      return async () => await this.switchPageAction(this.prevPage(currentPage));
    },
    handleDialogClose (data) {
      this.progressScenarioId = null;
      this.playerBuilder.reset(null);

      this.$utils.proxyEvent(
        this,
        EventTypes.DialogClose,
        CLOSE_SETUP_EVENT,
        data
      );
    },
    isLastStep (step) {
      return this.nextPage(step) === CLOSE_DIALOG;
    }
  }
};
